import { useEffect, useState, type FormEvent } from 'react';
import type { CardTxnRow, MeResponse, PosConnection, PosTerminal, PosTerminalInput } from '@kassa/shared';
import { api, ApiError } from '../../api';
import { adapterFor } from '../../pos/adapters';
import { DataTable } from '../../components/ListPage';

// Tənzimləmə → Terminallar (KT-91): bank POS terminalı printer kimi — obyekt üzrə, kassanın susmaya görə terminalı.
// Gün sonu (batch) terminalın cəmi sistemlə tutuşdurulur. Yoxlanmalı kart əməliyyatlarını menecer həll edir.

const CONNECTION_LABEL: Record<PosConnection, string> = { usb: 'USB', com: 'COM port', lan: 'LAN (IP:port)', cloud: 'Bankın bulud API-si', none: 'Qoşulmayıb' };
const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Xəta baş verdi');
/** Onluq sətir → «10,00» (float yoxdur). */
const az = (v: string) => v.replace('.', ',');

const empty = (outletId: string): PosTerminalInput => ({
  outletId,
  name: '',
  bank: null,
  model: null,
  connection: 'none',
  address: null,
  adapter: 'simulator',
  terminalTid: null,
  feePct: '0',
  settleDays: 0,
  active: true,
  defaultForRegisterIds: [],
});

export function TerminalsPanel({ me }: { me: MeResponse }) {
  const [rows, setRows] = useState<PosTerminal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; input: PosTerminalInput } | null>(null);
  const [batchMsg, setBatchMsg] = useState<{ text: string; bad: boolean } | null>(null);
  const canReview = me.permissions?.includes('approve.accounting') ?? false;

  const load = () =>
    api
      .posTerminals()
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) => setError(errText(e)));
  useEffect(() => void load(), []);

  // Terminalda bağlanmış batch-in nəticəsi serverə yazılana qədər saxlanır (terminal onu ikinci dəfə vermir)
  const pendingKey = (id: string) => `kassa.pos.batch.${id}`;
  const closeBatch = async (t: PosTerminal) => {
    const a = adapterFor(t);
    if (!a) return;
    setBatchMsg(null);
    let b: Awaited<ReturnType<typeof a.batchClose>> | null = null;
    try {
      const saved = window.localStorage.getItem(pendingKey(t.id));
      b = saved ? (JSON.parse(saved) as typeof b) : null;
    } catch {
      b = null;
    }
    try {
      if (!b) {
        b = await a.batchClose();
        try {
          window.localStorage.setItem(pendingKey(t.id), JSON.stringify(b));
        } catch {
          /* yaddaş yoxdur — nəticə yalnız bu ekranda */
        }
      }
      const r = await api.posBatch(t.id, b);
      try {
        window.localStorage.removeItem(pendingKey(t.id));
      } catch {
        /* yoxdur */
      }
      const open = r.openCount > 0 ? ` Diqqət: ${r.openCount} yarımçıq/yoxlanmalı əməliyyat cəmə düşmür.` : '';
      setBatchMsg(
        r.match
          ? { text: `${t.name}: batch ${r.batchNo} bağlandı — terminal və sistem eynidir (${r.count} əməliyyat, ${az(r.total)} ₼).${open}`, bad: r.openCount > 0 }
          : { text: `${t.name}: batch ${r.batchNo} — FƏRQ var: terminal ${az(r.total)} ₼, sistem ${az(r.systemTotal)} ₼. Yoxlanmalı əməliyyatlara baxın.${open}`, bad: true },
      );
    } catch (e) {
      setBatchMsg({
        text: b ? `Terminalda batch ${b.batchNo} bağlandı, sistemə yazılmadı (${errText(e)}) — «Batch bağla» ilə təkrar göndərin.` : errText(e),
        bad: true,
      });
    }
  };

  return (
    <section className="card" aria-label="Terminallar">
      <div className="card-head">
        <h2>Bank terminalları</h2>
        <button type="button" className="btn-secondary" onClick={() => setEditing({ id: null, input: empty(me.outlet?.id ?? '') })}>
          + Terminal
        </button>
      </div>
      <p className="muted small">
        Terminal printer kimi seçilir: kassada «Kart» yazanda məbləğ seçilmiş terminala avtomatik gedir. Kart nömrəsi saxlanmır — yalnız son 4 rəqəm, RRN
        və avtorizasiya kodu.
      </p>
      {editing && (
        <TerminalForm
          me={me}
          id={editing.id}
          initial={editing.input}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {batchMsg && (
        <p className={batchMsg.bad ? 'bad' : 'ok'} role={batchMsg.bad ? 'alert' : 'status'}>
          {batchMsg.text}
        </p>
      )}
      <DataTable
        label="Terminallar"
        loading={rows === null}
        error={error}
        rows={rows ?? []}
        rowKey={(t) => t.id}
        onEdit={(t) =>
          setEditing({
            id: t.id,
            input: { ...t, defaultForRegisterIds: t.defaultFor.map((d) => d.registerId) },
          })
        }
        editLabel="Terminalı redaktə et"
        empty={<p className="muted">Terminal yoxdur. Kart ödənişi əl ilə yazılır.</p>}
        columns={[
          { key: 'name', label: 'Ad', render: (t) => t.name },
          { key: 'bank', label: 'Bank', minor: true, render: (t) => t.bank ?? '—' },
          { key: 'conn', label: 'Qoşulma', minor: true, render: (t) => CONNECTION_LABEL[t.connection] },
          { key: 'adapter', label: 'Adapter', render: (t) => (t.adapter === 'simulator' ? 'Simulyator' : 'Əl ilə') },
          { key: 'def', label: 'Kassalar', minor: true, render: (t) => t.defaultFor.map((d) => d.code).join(', ') || '—' },
          {
            key: 'st',
            label: 'Status',
            render: (t) => <span className={`badge ${t.active ? 'badge-ok' : 'badge-off'}`}>{t.active ? 'Aktiv' : 'Deaktiv'}</span>,
          },
          {
            key: 'batch',
            label: 'Gün sonu',
            render: (t) =>
              t.adapter === 'simulator' && t.active ? (
                <button type="button" className="link-btn" onClick={() => void closeBatch(t)}>
                  Batch bağla
                </button>
              ) : (
                '—'
              ),
          },
        ]}
      />
      {canReview && <ReviewList terminals={rows ?? []} />}
    </section>
  );
}

function TerminalForm({ me, id, initial, onCancel, onSaved }: { me: MeResponse; id: string | null; initial: PosTerminalInput; onCancel: () => void; onSaved: () => void }) {
  const [f, setF] = useState<PosTerminalInput>(initial);
  const [registers, setRegisters] = useState<{ id: string; code: string; outletId: string }[]>([]);
  const [outlets, setOutlets] = useState<{ id: string; name: string }[]>(me.outlet ? [me.outlet] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const set = <K extends keyof PosTerminalInput>(k: K, v: PosTerminalInput[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api
      .shiftRegisters()
      .then((r) => setRegisters(r.map((x) => ({ id: x.id, code: x.code, outletId: x.outletId }))))
      .catch((e: unknown) => setLoadError(`Kassalar yüklənmədi: ${errText(e)}`));
    api
      .outlets()
      .then((o) => {
        if (o.length) setOutlets(o);
      })
      .catch((e: unknown) => setLoadError(`Obyektlər yüklənmədi: ${errText(e)}`));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.posTerminalSave(id, f);
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const outletRegs = registers.filter((r) => r.outletId === f.outletId);
  return (
    <form className="contract-form" onSubmit={(e) => void submit(e)} aria-label={id ? 'Terminalı redaktə et' : 'Yeni terminal'}>
      <label className="field">
        <span>Ad</span>
        <input value={f.name} onChange={(e) => set('name', e.target.value)} required maxLength={100} />
      </label>
      <label className="field">
        <span>Obyekt</span>
        <select value={f.outletId} onChange={(e) => setF((x) => ({ ...x, outletId: e.target.value, defaultForRegisterIds: [] }))}>
          {outlets.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Bank</span>
        <input value={f.bank ?? ''} onChange={(e) => set('bank', e.target.value || null)} maxLength={100} />
      </label>
      <label className="field">
        <span>Model</span>
        <input value={f.model ?? ''} onChange={(e) => set('model', e.target.value || null)} maxLength={100} />
      </label>
      <label className="field">
        <span>Qoşulma</span>
        <select value={f.connection} onChange={(e) => set('connection', e.target.value as PosConnection)}>
          {(Object.keys(CONNECTION_LABEL) as PosConnection[]).map((k) => (
            <option key={k} value={k}>
              {CONNECTION_LABEL[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Ünvan (COM3, 192.168.1.50:2000)</span>
        <input value={f.address ?? ''} onChange={(e) => set('address', e.target.value || null)} maxLength={200} />
      </label>
      <label className="field">
        <span>Adapter</span>
        <select value={f.adapter} onChange={(e) => set('adapter', e.target.value as PosTerminalInput['adapter'])}>
          <option value="simulator">Simulyator (sınaq)</option>
          <option value="manual">Əl ilə (inteqrasiyasız)</option>
        </select>
      </label>
      <label className="field">
        <span>Terminal ID</span>
        <input value={f.terminalTid ?? ''} onChange={(e) => set('terminalTid', e.target.value || null)} maxLength={40} />
      </label>
      <label className="field">
        <span>Komissiya, %</span>
        <input inputMode="decimal" value={f.feePct} onChange={(e) => set('feePct', e.target.value)} />
      </label>
      <label className="field">
        <span>Pul hesaba (T+N gün)</span>
        <input type="number" min={0} max={30} value={f.settleDays} onChange={(e) => set('settleDays', Number(e.target.value))} />
      </label>
      <fieldset className="field">
        <legend>Susmaya görə bu kassalarda</legend>
        {outletRegs.length === 0 && <span className="muted small">Bu obyektdə kassa yoxdur.</span>}
        {outletRegs.map((r) => (
          <label key={r.id} className="inline-check">
            <input
              type="checkbox"
              checked={f.defaultForRegisterIds.includes(r.id)}
              onChange={(e) => set('defaultForRegisterIds', e.target.checked ? [...f.defaultForRegisterIds, r.id] : f.defaultForRegisterIds.filter((x) => x !== r.id))}
            />{' '}
            {r.code}
          </label>
        ))}
      </fieldset>
      <label className="inline-check">
        <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Aktiv
      </label>
      {(error ?? loadError) && (
        <p className="bad" role="alert">
          {error ?? loadError}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={busy || !f.name.trim()}>
          Yadda saxla
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Ləğv et
        </button>
      </div>
    </form>
  );
}

function ReviewList({ terminals }: { terminals: PosTerminal[] }) {
  const [rows, setRows] = useState<CardTxnRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    api
      .posReview()
      .then(setRows)
      .catch((e: unknown) => setError(errText(e)));
  useEffect(() => void load(), []);

  const resolve = async (t: CardTxnRow, approved: boolean) => {
    if (!approved && !window.confirm(`${t.amount} ₼ əməliyyatı rədd edilsin? Bu qətidir — pul çıxıbsa bankın çıxarışına əvvəl baxın.`)) return;
    let body: Parameters<typeof api.posResolve>[1] = { status: 'declined', message: 'Menecer: bankın çıxarışında yoxdur' };
    if (approved) {
      const rrn = window.prompt('Terminal çekindəki RRN')?.trim() ?? '';
      const auth = window.prompt('Avtorizasiya kodu')?.trim() ?? '';
      const last4 = window.prompt('Kartın son 4 rəqəmi')?.trim() ?? '';
      if (!rrn || !auth) return;
      body = { status: 'approved', rrn, authCode: auth, cardLast4: last4 || null, message: 'Menecer: çekə görə təsdiq' };
    }
    try {
      await api.posResolve(t.id, body);
      void load();
    } catch (e) {
      setError(errText(e));
    }
  };

  // Satışa bağlanmamış təsdiq: pul terminalda çıxıb, sənəd yoxdur — terminalda ləğv (void)
  const voidTxn = async (t: CardTxnRow) => {
    const term = terminals.find((x) => x.id === t.terminalId);
    const a = term ? adapterFor(term) : null;
    if (!a || !t.rrn || !window.confirm(`${t.amount} ₼ terminalda ləğv edilsin (pul karta qayıdır)?`)) return;
    try {
      const id = crypto.randomUUID();
      await api.posStart({ id, terminalId: t.terminalId, registerId: t.registerId, kind: 'void', amount: t.amount, origTxnId: t.id });
      const res = await a.void({ txnId: id, origRrn: t.rrn }).catch(() => ({ status: 'unknown' as const }));
      await api.posReport(id, res);
      void load();
    } catch (e) {
      setError(errText(e));
    }
  };

  if (rows.length === 0 && !error) return null;
  return (
    <div aria-label="Yoxlanmalı kart əməliyyatları">
      <h3>Yoxlanmalı kart əməliyyatları</h3>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <ul className="contract-pending">
        {rows.map((t) => (
          <li key={t.id}>
            <span>
              {t.terminalName} · <span className="mono">{az(t.amount)} ₼</span> · {new Date(t.createdAt).toLocaleString('az-AZ')} ·{' '}
              {t.status !== 'approved' && <span className="badge badge-warn">{t.status === 'needs_review' ? 'Yoxlanır' : t.status === 'unknown' ? 'Bilinmir' : 'Yarımçıq'}</span>}
            </span>
            {t.status === 'approved' && (
              <span className="contract-annex-actions">
                <span className="badge badge-warn">Satışa bağlanmayıb</span>
                <button type="button" className="link-btn" onClick={() => void voidTxn(t)}>
                  Terminalda ləğv et
                </button>
              </span>
            )}
            {(t.status === 'needs_review' || t.status === 'unknown' || t.status === 'pending') && (
              <span className="contract-annex-actions">
                <button type="button" className="link-btn" onClick={() => void resolve(t, true)}>
                  Təsdiq (çekə görə)
                </button>
                <button type="button" className="link-btn" onClick={() => void resolve(t, false)}>
                  Rədd
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
